import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Bell,
  CheckCircle,
  AlertCircle,
  Info,
  Check,
} from 'lucide-react';
import { notificationAPI } from '../../services/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

// ── Icon / styling per notification type ──────────────────────────────────
const TYPE_CONFIG = {
  SUCCESS:        { Icon: CheckCircle, iconBg: 'bg-emerald-50', iconColor: 'text-emerald-500' },
  TASK_COMPLETED: { Icon: CheckCircle, iconBg: 'bg-emerald-50', iconColor: 'text-emerald-500' },
  WARNING:        { Icon: AlertCircle, iconBg: 'bg-amber-50',   iconColor: 'text-amber-500' },
  ERROR:          { Icon: AlertCircle, iconBg: 'bg-red-50',     iconColor: 'text-red-500' },
  INFO:           { Icon: Info,        iconBg: 'bg-blue-50',    iconColor: 'text-[#1C8DEE]' },
};

function getTypeConfig(type) {
  return (
    TYPE_CONFIG[(type || '').toUpperCase()] || {
      Icon: Bell,
      iconBg: 'bg-blue-50',
      iconColor: 'text-[#1C8DEE]',
    }
  );
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [markingAll, setMarkingAll]       = useState(false);

  useEffect(() => { fetchNotifications(); }, []);

  const fetchNotifications = async () => {
    try {
      const res = await notificationAPI.getAll({ limit: 50 });
      setNotifications(res.notifications || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async notificationId => {
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n.notificationId === notificationId ? { ...n, isRead: true } : n,
        ),
      );
    } catch (err) {
      toast.error('Could not mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Could not mark all as read');
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
            <p className="text-sm text-gray-500 mt-1">
              {unreadCount > 0
                ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                : "You're all caught up"}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllRead}
              disabled={markingAll}
              className="border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg text-sm"
            >
              <Check className="w-4 h-4 mr-1.5" />
              {markingAll ? 'Marking…' : 'Mark all as read'}
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-center">
            <p className="text-2xl font-bold text-gray-900">{notifications.length}</p>
            <p className="text-sm text-gray-500 mt-0.5">Total</p>
          </div>
          <div className="bg-white rounded-xl border border-[#1C8DEE]/20 p-4 shadow-sm text-center">
            <p className="text-2xl font-bold text-[#1C8DEE]">{unreadCount}</p>
            <p className="text-sm text-gray-500 mt-0.5">Unread</p>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className="h-16 bg-white rounded-xl border border-gray-100 animate-pulse shadow-sm"
              />
            ))}
          </div>
        ) : notifications.length > 0 ? (
          <Card className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-50">
              {notifications.map((notif, i) => {
                const { Icon, iconBg, iconColor } = getTypeConfig(notif.type);
                return (
                  <div
                    key={notif.notificationId || i}
                    className={`flex items-start gap-4 px-5 py-4 transition-colors ${
                      !notif.isRead ? 'bg-blue-50/40 hover:bg-blue-50/60' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                      <Icon className={`w-4 h-4 ${iconColor}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium leading-snug ${
                          !notif.isRead ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {notif.title}
                        </p>
                        {!notif.isRead && (
                          <span className="w-2 h-2 rounded-full bg-[#1C8DEE] flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      {notif.message && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                      )}
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs text-gray-400">
                          {notif.createdAt
                            ? formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })
                            : ''}
                        </span>
                        {!notif.isRead && notif.notificationId && (
                          <button
                            onClick={() => handleMarkRead(notif.notificationId)}
                            className="text-xs text-[#1C8DEE] hover:underline font-medium"
                          >
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ) : (
          /* Empty state */
          <div className="text-center py-20 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Bell className="w-7 h-7 text-gray-300" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">No notifications yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              You'll receive updates here as your applications progress
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
